import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import {
  Plus,
  Edit,
  Trash2,
  Archive,
  ArchiveRestore,
  Ticket,
  BarChart3,
  MoreVertical,
  Search,
  Loader2,
  ExternalLink,
  Filter,
  X,
  Copy,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { DateTimeInput } from '../components/ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../lib/dateInputs';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { isCourseEvent } from '../lib/eventTypes';
import apiService from '../services/api.service';
import { PUBLIC_SITE_URL } from '../config/api';
import { Event, EventForm } from '../types';
import { useEvent } from '../contexts/EventContext';

// ─────────────────────────────────────────────────────────────────────────────
// Liste des événements — briques partagées par la vue bureau et la vue mobile
// ─────────────────────────────────────────────────────────────────────────────

/** Où se situe l'événement dans le temps. C'est ce qui structure la liste :
 *  on ne cherche pas la même chose dans un événement passé et dans le prochain. */
type EventBucket = 'ongoing' | 'upcoming' | 'past';

/** Ordre de tri du statut : ce qui vit en premier. */
const STATUS_ORDER = ['active', 'published', 'draft', 'completed', 'finished', 'archived'];

const startOfDay = (d: Date): Date => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const eventStartDate = (event: Event): Date => new Date(event.date_start || event.date);
const eventEndDate = (event: Event): Date => new Date(event.date_end || event.date_start || event.date);

/** Horodatage de départ, avec repli : un événement sans date valide ne doit pas
 *  rendre le tri instable (NaN casse la comparaison), il part à la fin. */
const eventStartTs = (event: Event): number => {
  const ts = eventStartDate(event).getTime();
  return Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts;
};

const bucketOfEvent = (event: Event, today: Date): EventBucket => {
  const start = eventStartDate(event);
  if (Number.isNaN(start.getTime())) return 'upcoming';
  const end = eventEndDate(event);
  const endDay = Number.isNaN(end.getTime()) ? startOfDay(start) : startOfDay(end);
  if (endDay < today) return 'past';
  return startOfDay(start) <= today ? 'ongoing' : 'upcoming';
};

/** La ville suffit à situer un événement — l'adresse postale complète est la
 *  raison pour laquelle une ligne pesait quatre lignes de haut. */
const cityOfEvent = (event: Event): string =>
  (event.location_city || '').trim() || (event.location || '').trim() || '—';

/** Le logo est servi par l'API, pas par pro.logfit.eu : une URL relative
 *  « /api/… » doit être préfixée par l'hôte de l'API, sinon 404 en production. */
const getEventLogoFullUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('/api')) {
    const baseUrl = (process.env.REACT_APP_API_URL || 'https://events.logfit.eu/api/v1').replace('/api/v1', '');
    return `${baseUrl}${url}`;
  }
  return url;
};

/** Initiales de repli quand l'événement n'a pas de logo. */
const initialsOfEvent = (name: string): string => {
  const words = (name || '')
    .split(/[\s\-_/]+/)
    .map(w => w.replace(/[^A-Za-zÀ-ÿ0-9]/g, ''))
    .filter(Boolean);
  if (!words.length) return '?';
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

/** Pastille de statut : la couleur DIT l'état, elle ne remplit pas un rectangle
 *  (charte — plus d'aplat saturé). */
const statusDotClass = (status: string): string => {
  switch (status) {
    case 'active': return 'bg-success shadow-[0_0_0_3px_rgba(34,197,94,0.16)]';
    case 'published': return 'bg-neutral-200';
    case 'draft': return 'border-[1.5px] border-neutral-500 bg-transparent';
    default: return 'bg-neutral-600';
  }
};

const CHIP_CLASS =
  'inline-flex shrink-0 items-center rounded-[5px] border px-1.5 py-px text-[10px] font-semibold tracking-[0.04em]';

const CIRCLE_ACTION_CLASS =
  'grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border border-white/[0.14] ' +
  'text-muted-foreground transition-colors duration-150 hover:border-white/40 hover:text-foreground ' +
  'disabled:pointer-events-none disabled:opacity-30 ' +
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60';

/** Action rapide d'une ligne : icône cerclée, grise au repos, blanche au survol. */
const CircleAction: React.FC<{
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, disabled, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    className={CIRCLE_ACTION_CLASS}
  >
    {children}
  </button>
);

/** En-tête de colonne triable. Défini hors du composant : recréé à chaque
 *  rendu, il perdrait le focus clavier juste après le clic qui l'a activé. */
const SortHeader: React.FC<{
  col: string;
  label: string;
  align?: 'left' | 'right';
  sortKey: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (col: any) => void;
}> = ({ col, label, align = 'left', sortKey, sortDir, onSort }) => (
  <button
    type="button"
    onClick={() => onSort(col)}
    className={cn(
      'flex items-center gap-1 rounded-sm text-[10px] font-semibold uppercase tracking-[0.12em]',
      'text-neutral-500 transition-colors hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60',
      align === 'right' && 'justify-end'
    )}
  >
    {label}
    {sortKey !== col ? (
      <ArrowUpDown size={11} className="opacity-40" />
    ) : sortDir === 'asc' ? (
      <ArrowUp size={11} />
    ) : (
      <ArrowDown size={11} />
    )}
  </button>
);

/** Barre de remplissage des inscriptions. Elle passe en or au-delà de 75 % :
 *  l'or est réservé aux données de valorisation (charte §1). */
const FillBar: React.FC<{ percent: number | null }> = ({ percent }) => (
  <span className="h-1 w-11 shrink-0 overflow-hidden rounded-full bg-white/10">
    <span
      className={cn('block h-full rounded-full', (percent ?? 0) >= 75 ? 'bg-[#facc15]' : 'bg-neutral-200')}
      style={{ width: `${percent ?? 0}%` }}
    />
  </span>
);

const Events: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedEventId, setSelectedEventId, refreshEvents } = useEvent();

  const navigateToRegistrations = (eventId: string, tab?: 'config' | 'responses') => {
    setSelectedEventId(eventId);
    navigate(`/admin/registrations${tab ? `?tab=${tab}` : ''}`);
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // États pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');


  const hasActiveFilters = search !== '' || typeFilter !== 'all' || statusFilter !== 'all';

  const formatError = (err: any): string => {
    const errorMsg = err.response?.data?.detail;
    if (typeof errorMsg === 'string') {
      return errorMsg;
    } else if (Array.isArray(errorMsg)) {
      return errorMsg.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    } else if (errorMsg && typeof errorMsg === 'object') {
      return JSON.stringify(errorMsg);
    } else {
      return err.message || t('common.unknownError');
    }
  };

  const [formData, setFormData] = useState<EventForm>({
    name: '',
    event_type: 'crossfit',
    date_start: '',
    date_end: '',
    description: '',
    location: '',
    location_street: '',
    location_postal_code: '',
    location_city: '',
    location_country: '',
    venue_name: '',
    status: 'draft',
    visibility: 'public',
    max_participants: 100,
    lanes_count: 8
  });

  useEffect(() => {
    loadEvents();
  }, []);

  // Gestion retour Stripe onboarding
  useEffect(() => {
    const stripeOnboarding = searchParams.get('stripe_onboarding');
    if (stripeOnboarding === 'success') {
      setError('');
      setSearchParams(prev => {
        prev.delete('stripe_onboarding');
        return prev;
      });
    }
  }, [searchParams, setSearchParams]);

  // Auto-ouvrir le dialog de création si paramètre URL create=true
  useEffect(() => {
    const shouldCreate = searchParams.get('create');
    if (shouldCreate === 'true' && !openDialog) {
      handleOpenDialog();
      setSearchParams(prev => {
        prev.delete('create');
        return prev;
      });
    }
  }, [searchParams, openDialog]);

  // Ancien lien ?edit={eventId} : l'édition vit désormais sur sa page.
  useEffect(() => {
    const editEventId = searchParams.get('edit');
    if (editEventId) navigate(`/admin/events/${editEventId}/settings`, { replace: true });
  }, [searchParams, navigate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await apiService.getEvents();
      setEvents(response.data || []);
    } catch (err: any) {
      setError(t('errors.generic'));
      console.error('Erreur chargement événements:', err);
    } finally {
      setLoading(false);
    }
  };

  const emptyForm = (): EventForm => ({
    name: '',
    event_type: 'crossfit',
    date_start: '',
    date_end: '',
    description: '',
    location: '',
    location_street: '',
    location_postal_code: '',
    location_city: '',
    location_country: '',
    venue_name: '',
    status: 'draft',
    visibility: 'public',
    max_participants: 100,
    lanes_count: 8
  });

  // La fenêtre ne sert plus qu'à CRÉER (nom, type, dates) : le reste se règle
  // sur la page « Paramètres de l'événement », où l'on arrive juste après.
  const handleOpenDialog = () => {
    setFormData(emptyForm());
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const openSettings = (eventId: string) => {
    setSelectedEventId(eventId);
    navigate(`/admin/events/${eventId}/settings`);
  };

  const handleSubmit = async () => {
    if (refuseIfDateInputIncomplete(t)) return;
    setSaving(true);
    try {
      const res = await apiService.createEvent(formData);
      const newId: string | undefined = res?.data?.id;
      handleCloseDialog();
      await refreshEvents();
      if (newId) {
        openSettings(newId);
      } else {
        loadEvents();
      }
    } catch (err: any) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (event: Event) => {
    try {
      await apiService.archiveEvent(event.id);
      loadEvents();
    } catch (err: any) {
      setError(formatError(err));
    }
  };

  const handleRestore = async (event: Event) => {
    try {
      await apiService.restoreEvent(event.id);
      loadEvents();
    } catch (err: any) {
      setError(formatError(err));
    }
  };

  const handleDelete = (event: Event) => setEventToDelete(event);

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    setDeleting(true);
    try {
      await apiService.deleteEvent(eventToDelete.id);
      setEventToDelete(null);
      loadEvents();
    } catch (err: any) {
      setError(formatError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (event: Event) => {
    try {
      const response = await apiService.duplicateEvent(event.id);
      loadEvents();
      toast.success(t('events.duplicateSuccess', { name: response.data.new_event.name }));
    } catch (err: any) {
      setError(formatError(err));
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return t('common.draft');
      case 'published': return t('common.published');
      case 'active': return t('common.active');
      case 'completed': return t('events.completed');
      case 'finished': return t('events.finished');
      case 'archived': return t('events.archived');
      default: return status;
    }
  };

  // ── Tri des colonnes ───────────────────────────────────────────────────────
  // Trois états : croissant → décroissant → retour à l'ordre chronologique
  // naturel (à venir croissant, passés décroissant), qui est le défaut voulu.
  type SortKey = 'name' | 'type' | 'date' | 'city' | 'status' | 'registrations';
  type SortDir = 'asc' | 'desc';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
      setSortDir('asc');
    }
  };

  const today = React.useMemo(() => startOfDay(new Date()), []);

  // ── Formats compacts ───────────────────────────────────────────────────────
  const shortDate = (d: Date, withYear: boolean): string =>
    d.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
    });

  /** « 24 oct. », « 3 – 4 oct. », « 1 sept. → 7 oct. ». L'année n'apparaît que
   *  si l'événement n'est pas dans l'année courante — sinon elle est du bruit. */
  const formatCompactRange = (event: Event): string => {
    const start = eventStartDate(event);
    if (Number.isNaN(start.getTime())) return t('events.dateNotDefined');
    const end = eventEndDate(event);
    const currentYear = new Date().getFullYear();
    const withYear =
      start.getFullYear() !== currentYear ||
      (!Number.isNaN(end.getTime()) && end.getFullYear() !== currentYear);

    if (Number.isNaN(end.getTime()) || startOfDay(start).getTime() === startOfDay(end).getTime()) {
      return shortDate(start, withYear);
    }
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.getDate()} – ${shortDate(end, withYear)}`;
    }
    return `${shortDate(start, false)} → ${shortDate(end, withYear)}`;
  };

  /** Nombre de jours avant le départ, ou null si l'événement a déjà commencé. */
  const daysToGo = (event: Event): number | null => {
    const start = eventStartDate(event);
    if (Number.isNaN(start.getTime())) return null;
    const diff = Math.round((startOfDay(start).getTime() - today.getTime()) / 86400000);
    return diff > 0 ? diff : null;
  };

  // ── Filtrage ───────────────────────────────────────────────────────────────
  // Les archivés ont leur propre page : les laisser ici rendait le geste
  // « archiver » sans effet visible.
  const visibleEvents = React.useMemo(() => events.filter((event) => {
    if (statusFilter !== 'archived' && event.status === 'archived') return false;
    if (search) {
      const needle = search.toLowerCase();
      const haystack = [event.name, event.location_city, event.location, event.venue_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (typeFilter !== 'all' && event.event_type !== typeFilter) return false;
    if (statusFilter !== 'all' && event.status !== statusFilter) return false;
    return true;
  }), [events, search, typeFilter, statusFilter]);

  // ── Regroupement temporel + tri ────────────────────────────────────────────
  const eventGroups = React.useMemo(() => {
    const buckets: Record<EventBucket, Event[]> = { ongoing: [], upcoming: [], past: [] };
    visibleEvents.forEach(event => buckets[bucketOfEvent(event, today)].push(event));

    const compare = (a: Event, b: Event, bucket: EventBucket): number => {
      if (!sortKey) {
        const chrono = eventStartTs(a) - eventStartTs(b);
        // Passés : du plus récent au plus ancien. À venir : le prochain d'abord.
        return bucket === 'past' ? -chrono : chrono;
      }
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'name':
          return dir * a.name.localeCompare(b.name, i18n.language, { sensitivity: 'base' });
        case 'type':
          return dir * (a.event_type || '').localeCompare(b.event_type || '');
        case 'date':
          return dir * (eventStartTs(a) - eventStartTs(b));
        case 'city':
          return dir * cityOfEvent(a).localeCompare(cityOfEvent(b), i18n.language, { sensitivity: 'base' });
        case 'status':
          return dir * (STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
        case 'registrations':
          return dir * ((a.registrations_count ?? 0) - (b.registrations_count ?? 0));
        default:
          return 0;
      }
    };

    return (['ongoing', 'upcoming', 'past'] as EventBucket[])
      .map(bucket => ({ bucket, events: [...buckets[bucket]].sort((a, b) => compare(a, b, bucket)) }))
      .filter(group => group.events.length > 0);
  }, [visibleEvents, sortKey, sortDir, today, i18n.language]);

  const sortProps = { sortKey, sortDir, onSort: handleSort };

  /** Une ligne de la liste. Fonction (pas composant) : appelée dans le rendu,
   *  elle ne crée pas un nouveau type à chaque passe. */
  const renderEventRow = (event: Event, bucket: EventBucket) => {
    const isPast = bucket === 'past';
    const isSelected = selectedEventId === event.id;
    const jours = bucket === 'upcoming' ? daysToGo(event) : null;
    const count = event.registrations_count ?? 0;
    const capacity = event.registrations_capacity ?? null;
    const percent = capacity && capacity > 0 ? Math.min(100, Math.round((count / capacity) * 100)) : null;
    const publicUrl = event.slug ? `${PUBLIC_SITE_URL}/events/${event.slug}` : null;
    const resultsPath = event.event_type === 'crossfit'
      ? `/admin/events/${event.id}/crossfit-results`
      : `/admin/events/${event.id}/race-results`;
    const planningLabel = isCourseEvent(event.event_type) ? t('nav.waves') : t('nav.heats');
    // Le fichier est servi par l'API, pas par pro.logfit.eu : une URL relative
    // en `/api/...` doit être préfixée par l'hôte de l'API, sinon 404.
    const logoUrl = getEventLogoFullUrl(event.logo_url ?? null);

    return (
      <div
        key={event.id}
        className={cn(
          'event-row event-line border-b border-white/[0.08] px-2 py-[9px] transition-colors duration-150',
          isSelected ? 'bg-white/[0.06] shadow-[inset_2px_0_0_0_#fff]' : 'hover:bg-white/[0.05]'
        )}
      >
        {/* Vignette : le logo de l'événement, sinon ses initiales. Le liseré
            vert dit « c'est celui-ci qui tourne » sans un badge de plus. */}
        <div className={cn(
          'relative grid h-8 w-8 place-items-center overflow-hidden rounded-lg border border-white/[0.14]',
          'bg-white/[0.04] text-[11px] font-semibold uppercase text-muted-foreground',
          isPast && 'opacity-50'
        )}>
          <span>{initialsOfEvent(event.name)}</span>
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {bucket === 'ongoing' && (
            <span aria-hidden className="pointer-events-none absolute -inset-px rounded-lg border-[1.5px] border-success/60" />
          )}
        </div>

        {/* Nom — le clic choisit l'événement de travail (le sélecteur de la
            barre latérale suit). Modifier vit dans le menu « … ». */}
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedEventId(event.id)}
            title={t('events.list.selectHint')}
            className={cn(
              'truncate rounded-sm text-left text-[13.5px] tracking-[-0.005em]',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60',
              isPast ? 'font-medium text-muted-foreground' : 'font-semibold text-foreground'
            )}
          >
            {event.name}
          </button>
          {event.visibility === 'private' && (
            <span className={cn(CHIP_CLASS, 'border-white/[0.14] text-muted-foreground')}>
              {t('events.list.private')}
            </span>
          )}
        </div>

        <span className="truncate text-[12.5px] text-muted-foreground">
          {event.event_type === 'crossfit' ? t('events.crossfit') : t('events.course')}
        </span>

        <span className="whitespace-nowrap text-[12.5px] tabular-nums text-muted-foreground">
          {formatCompactRange(event)}
          {jours !== null && (
            <span className="text-neutral-500"> · {t('events.list.daysToGo', { count: jours })}</span>
          )}
        </span>

        <span className="truncate text-[12.5px] text-muted-foreground" title={event.location || undefined}>
          {cityOfEvent(event)}
        </span>

        <span className="flex items-center gap-2 whitespace-nowrap text-[12.5px] text-muted-foreground">
          <i className={cn('h-[7px] w-[7px] shrink-0 rounded-full', statusDotClass(event.status))} />
          {getStatusLabel(event.status)}
        </span>

        {/* Inscrits — le chiffre qu'on vient chercher. La barre se lit en
            diagonale ; elle passe en or au-delà de 75 %. */}
        <span className="flex items-center justify-end gap-2 whitespace-nowrap text-[12.5px] tabular-nums">
          {isPast ? (
            <span className="text-muted-foreground">
              {count > 0 ? t('events.list.registeredCount', { count }) : '—'}
            </span>
          ) : (
            <>
              {percent !== null && <FillBar percent={percent} />}
              {capacity ? (
                <span className="text-foreground">
                  {count}<span className="text-neutral-500">/{capacity}</span>
                </span>
              ) : count > 0 ? (
                <span className="text-foreground">{count}</span>
              ) : (
                <span className="text-neutral-500">—</span>
              )}
            </>
          )}
        </span>

        {/* Les trois gestes réels, hors du menu « … ». */}
        <div className="event-actions flex justify-end gap-[5px]">
          {isPast ? (
            <CircleAction title={t('events.viewResults')} onClick={() => navigate(resultsPath)}>
              <BarChart3 size={13} />
            </CircleAction>
          ) : (
            <>
              <CircleAction title={t('nav.registrations')} onClick={() => navigateToRegistrations(event.id)}>
                <Ticket size={13} />
              </CircleAction>
              <CircleAction
                title={planningLabel}
                onClick={() => { setSelectedEventId(event.id); navigate('/admin/heats'); }}
              >
                <CalendarDays size={13} />
              </CircleAction>
            </>
          )}
          <CircleAction
            title={t('events.list.publicPage')}
            disabled={!publicUrl || event.status === 'draft'}
            onClick={() => publicUrl && window.open(publicUrl, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink size={13} />
          </CircleAction>
          <DropdownMenu>
            <DropdownMenuTrigger className={CIRCLE_ACTION_CLASS} aria-label={t('common.actions')}>
              <MoreVertical size={13} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openSettings(event.id)}>
                <Edit size={16} className="mr-2" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigateToRegistrations(event.id)}>
                <Ticket size={16} className="mr-2" />
                {t('nav.registrations')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(resultsPath)}>
                <BarChart3 size={16} className="mr-2" />
                {t('events.viewResults')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDuplicate(event)}>
                <Copy size={16} className="mr-2" />
                {t('common.duplicate')}
              </DropdownMenuItem>
              {event.status !== 'archived' ? (
                <DropdownMenuItem onClick={() => handleArchive(event)}>
                  <Archive size={16} className="mr-2" />
                  {t('common.archive')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => handleRestore(event)}>
                  <ArchiveRestore size={16} className="mr-2" />
                  {t('common.restore')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => handleDelete(event)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 size={16} className="mr-2" />
                {t('events.deleteForever')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  // Total listable = tout sauf les archivés (sauf si on les filtre exprès).
  const listableCount = React.useMemo(
    () => statusFilter === 'archived'
      ? events.length
      : events.filter(e => e.status !== 'archived').length,
    [events, statusFilter]
  );

  const hasNoEvent = visibleEvents.length === 0;
  const emptyLabel = hasActiveFilters ? t('events.noMatchingEvents') : t('events.noEvents');


  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-border flex-wrap">
        {/* Compteur — à gauche */}
        <div className="flex gap-2 items-center flex-wrap">
          <Badge variant="outline" className="flex items-center gap-1">
            <Ticket size={14} />
            {/* Le compteur annonce ce qui est RÉELLEMENT listé : les archivés
                ont leur propre page, les inclure ici afficherait « 10 » au-dessus
                de 9 lignes. */}
            {hasActiveFilters
              ? `${visibleEvents.length}/${listableCount}`
              : t('events.count', { count: listableCount })}
          </Badge>
        </div>

        {/* Actions - Droite */}
        <div className="flex gap-2 items-center flex-wrap">
          {/* Recherche extensible : la loupe est à même la barre, pas enfouie
              sous « Filtres ». Même gabarit que la page Équipes. */}
          <div
            className={cn(
              'inline-flex h-9 items-center overflow-hidden rounded-md border bg-background transition-[width] duration-200 ease-out',
              searchExpanded ? 'w-56' : 'w-9',
              search ? 'border-primary' : 'border-border'
            )}
          >
            <button
              type="button"
              onClick={() => {
                if (searchExpanded) {
                  setSearchExpanded(false);
                  setSearch('');
                } else {
                  setSearchExpanded(true);
                  setTimeout(() => searchInputRef.current?.focus(), 50);
                }
              }}
              className="flex h-full w-9 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
              aria-label={t('events.filters.searchPlaceholder')}
            >
              <Search size={16} />
            </button>
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearch('');
                  setSearchExpanded(false);
                }
              }}
              onBlur={() => {
                if (!search) setSearchExpanded(false);
              }}
              placeholder={t('events.filters.searchPlaceholder')}
              className="h-full w-full min-w-0 bg-transparent pr-1 text-sm text-foreground outline-none placeholder:text-muted-foreground"
              tabIndex={searchExpanded ? 0 : -1}
            />
            {search && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSearch('');
                  searchInputRef.current?.focus();
                }}
                className="flex h-full w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                aria-label={t('common.clear', 'Effacer')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              hasActiveFilters && "border-primary text-foreground"
            )}
          >
            <Filter size={18} />
            <span className="ml-2 whitespace-nowrap">
              {t('common.filters')}
            </span>
            {hasActiveFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" />
            )}
          </Button>
          <Button
            size="sm"
            onClick={() => handleOpenDialog()}
          >
            <Plus size={18} />
            <span className="ml-2 whitespace-nowrap">
              {t('events.newEvent')}
            </span>
          </Button>
        </div>
      </div>

      {/* Filtres — la recherche a rejoint la barre, il ne reste que les
          sélecteurs. */}
      <div className={cn(
        "grid transition-[grid-template-rows,opacity,margin] duration-200 ease-out",
        showFilters ? "grid-rows-[1fr] opacity-100 mb-4" : "grid-rows-[0fr] opacity-0"
      )}>
        <div className="min-h-0 overflow-hidden flex flex-col md:flex-row gap-3 pb-4 border-b border-border">
          <div className="flex gap-3">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="flex-1 md:w-[170px]">
                <SelectValue placeholder={t('common.type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('events.filters.allTypes')}</SelectItem>
                <SelectItem value="crossfit">{t('events.crossfit')}</SelectItem>
                <SelectItem value="course">{t('events.course')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="flex-1 md:w-[170px]">
                <SelectValue placeholder={t('events.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('events.filters.allStatuses')}</SelectItem>
                <SelectItem value="draft">{t('common.draft')}</SelectItem>
                <SelectItem value="published">{t('common.published')}</SelectItem>
                <SelectItem value="active">{t('common.active')}</SelectItem>
                <SelectItem value="completed">{t('events.completed')}</SelectItem>
                <SelectItem value="archived">{t('events.archived')}</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setSearchExpanded(false);
                  setTypeFilter('all');
                  setStatusFilter('all');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-sm underline">{t('common.close')}</button>
          </AlertDescription>
        </Alert>
      )}

      {/* Bureau : une ligne = un événement, colonnes alignées.
          C'est l'alignement vertical qui permet de balayer la liste ; la
          vignette et les actions rapides évitent d'entrer dans l'événement
          pour les trois gestes qu'on y fait vraiment. */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="event-grid">
          {/* En-têtes triables */}
          <div className="event-row border-b border-border px-2 py-2">
            <span />
            <SortHeader col="name" label={t('common.name')} {...sortProps} />
            <SortHeader col="type" label={t('common.type')} {...sortProps} />
            <SortHeader col="date" label={t('common.date')} {...sortProps} />
            <SortHeader col="city" label={t('events.city')} {...sortProps} />
            <SortHeader col="status" label={t('common.status')} {...sortProps} />
            <SortHeader col="registrations" label={t('events.list.registered')} align="right" {...sortProps} />
            <span />
          </div>

          {hasNoEvent ? (
            <div className="py-12 text-center text-muted-foreground">
              <p className="text-lg font-medium mb-2">{emptyLabel}</p>
              {!hasActiveFilters && (
                <Button variant="outline" onClick={() => handleOpenDialog()}>
                  <Plus size={18} className="mr-2" />
                  {t('events.createFirstEvent')}
                </Button>
              )}
            </div>
          ) : (
            eventGroups.map(group => (
              <div key={group.bucket}>
                <div className="flex items-center gap-3 px-2 pb-1.5 pt-5">
                  <span className={cn(
                    'text-[10.5px] font-semibold uppercase tracking-[0.14em]',
                    group.bucket === 'ongoing' ? 'text-success' : 'text-neutral-500'
                  )}>
                    {t(`events.list.group.${group.bucket}`)} · {group.events.length}
                  </span>
                  <span className="h-px flex-1 bg-white/[0.08]" />
                </div>
                {group.events.map(event => renderEventRow(event, group.bucket))}
              </div>
            ))
          )}
        </div>
      </div>
      {/* Mobile : mêmes groupes, mêmes signaux, en cartes. */}
      <div className="lg:hidden space-y-3">
        {hasNoEvent ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-lg font-medium mb-2 text-muted-foreground">{emptyLabel}</p>
              {!hasActiveFilters && (
                <Button variant="outline" onClick={() => handleOpenDialog()}>
                  <Plus size={18} className="mr-2" />
                  {t('events.createFirstEvent')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          eventGroups.map(group => (
            <div key={group.bucket} className="space-y-3">
              <div className="flex items-center gap-3 pt-2">
                <span className={cn(
                  'text-[10.5px] font-semibold uppercase tracking-[0.14em]',
                  group.bucket === 'ongoing' ? 'text-success' : 'text-neutral-500'
                )}>
                  {t(`events.list.group.${group.bucket}`)} · {group.events.length}
                </span>
                <span className="h-px flex-1 bg-white/[0.08]" />
              </div>

              {group.events.map((event) => {
                const isPast = group.bucket === 'past';
                const count = event.registrations_count ?? 0;
                const capacity = event.registrations_capacity ?? null;
                const percent = capacity && capacity > 0
                  ? Math.min(100, Math.round((count / capacity) * 100))
                  : null;
                const resultsPath = event.event_type === 'crossfit'
                  ? `/admin/events/${event.id}/crossfit-results`
                  : `/admin/events/${event.id}/race-results`;
                const logoUrl = getEventLogoFullUrl(event.logo_url ?? null);

                return (
                  <Card key={event.id}>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            'relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg',
                            'border border-white/[0.14] bg-white/[0.04] text-[11px] font-semibold uppercase text-muted-foreground',
                            isPast && 'opacity-50'
                          )}>
                            <span>{initialsOfEvent(event.name)}</span>
                            {logoUrl && (
                              <img
                                src={logoUrl}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                            {group.bucket === 'ongoing' && (
                              <span aria-hidden className="pointer-events-none absolute -inset-px rounded-lg border-[1.5px] border-success/60" />
                            )}
                          </div>

                          <button
                            onClick={() => setSelectedEventId(event.id)}
                            className="min-w-0 flex-1 text-left font-semibold"
                          >
                            {event.name}
                            {event.visibility === 'private' && (
                              <span className={cn(CHIP_CLASS, 'ml-2 border-white/[0.14] text-muted-foreground')}>
                                {t('events.list.private')}
                              </span>
                            )}
                          </button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 shrink-0 p-0">
                                <MoreVertical size={18} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openSettings(event.id)}>
                                <Edit size={16} className="mr-2" />
                                {t('common.edit')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigateToRegistrations(event.id)}>
                                <Ticket size={16} className="mr-2" />
                                {t('nav.registrations')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(resultsPath)}>
                                <BarChart3 size={16} className="mr-2" />
                                {t('events.viewResults')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicate(event)}>
                                <Copy size={16} className="mr-2" />
                                {t('common.duplicate')}
                              </DropdownMenuItem>
                              {event.status !== 'archived' ? (
                                <DropdownMenuItem onClick={() => handleArchive(event)}>
                                  <Archive size={16} className="mr-2" />
                                  {t('common.archive')}
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleRestore(event)}>
                                  <ArchiveRestore size={16} className="mr-2" />
                                  {t('common.restore')}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDelete(event)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 size={16} className="mr-2" />
                                {t('common.delete')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <i className={cn('h-[7px] w-[7px] shrink-0 rounded-full', statusDotClass(event.status))} />
                          {getStatusLabel(event.status)}
                          <span className="text-neutral-600">·</span>
                          {event.event_type === 'crossfit' ? t('events.crossfit') : t('events.course')}
                        </div>

                        <div className="space-y-1 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t('common.date')}</span>
                            <span className="font-medium tabular-nums">{formatCompactRange(event)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t('events.city')}</span>
                            <span className="font-medium">{cityOfEvent(event)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t('events.list.registered')}</span>
                            <span className="flex items-center gap-2 font-medium tabular-nums">
                              {!isPast && percent !== null && <FillBar percent={percent} />}
                              {capacity
                                ? <>{count}<span className="text-neutral-500">/{capacity}</span></>
                                : count > 0
                                  ? count
                                  : <span className="text-neutral-500">—</span>}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ))
        )}
      </div>
      {/* Création — nom, type, dates. Tout le reste (lieu, description,
          publication, invitations, logo) se règle ensuite sur la page
          « Paramètres de l'événement », ouverte juste après la création. */}
      <Dialog open={openDialog} onOpenChange={(o) => { if (!o && !saving) handleCloseDialog(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('events.newEvent')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">{t('events.eventName')}</Label>
              <Input
                id="name"
                autoFocus
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('events.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('events.eventType')}</Label>
              <Select
                value={formData.event_type}
                onValueChange={(value) => setFormData({ ...formData, event_type: value as any })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="crossfit">{t('events.crossfit')}</SelectItem>
                  <SelectItem value="course">{t('events.course')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_start">{t('events.startDate')}</Label>
                <DateTimeInput
                  id="date_start"
                  type="date"
                  value={formData.date_start}
                  onChange={(e) => setFormData({
                    ...formData,
                    date_start: e.target.value,
                    // Un événement d'un jour : la fin suit le début tant qu'on ne l'a pas choisie.
                    date_end: !formData.date_end || formData.date_end < e.target.value ? e.target.value : formData.date_end,
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_end">{t('events.endDate')}</Label>
                <DateTimeInput
                  id="date_end"
                  type="date"
                  value={formData.date_end}
                  onChange={(e) => setFormData({ ...formData, date_end: e.target.value })}
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">{t('eventSettings.createHint')}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={saving || !formData.name.trim() || !formData.date_start || !formData.date_end}>
              {saving && <Loader2 className="animate-spin" />}
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={eventToDelete !== null}
        onOpenChange={(o) => { if (!o) setEventToDelete(null); }}
        title={t('common.confirmDeleteTitle')}
        message={t('events.deleteConfirm', { name: eventToDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
      />

    </div>
  );
};

export default Events;
